import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Users,
  Download,
  RefreshCw,
  Loader2,
  FlaskConical,
  CheckCircle2,
  XCircle,
  Mail,
  MessageSquare,
  ShoppingCart,
  HelpCircle,
  X,
  Sparkles,
  AlertTriangle,
  Lightbulb
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AiRecommendation = ({ rec }) => {
  const p = (rec.priority || '').toLowerCase();
  let badgeStyle = 'bg-slate-100 text-slate-700';
  if (p === 'kritisk') badgeStyle = 'bg-red-100 text-red-700';
  else if (p === 'hoej' || p === 'høj') badgeStyle = 'bg-orange-100 text-orange-700';
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="flex items-start justify-between gap-2 mb-1">
        <p className="font-medium text-slate-800 text-sm">{rec.action}</p>
        <span className={`text-xs px-2 py-0.5 rounded font-medium flex-shrink-0 ${badgeStyle}`}>
          {rec.priority}
        </span>
      </div>
      {rec.why && (
        <p className="text-xs text-slate-500 mt-1">{rec.why}</p>
      )}
    </div>
  );
};

const AiAnalysisModal = ({ open, onClose, loading, result, error, onRefresh }) => {
  if (!open) return null;
  let cachedAt = null;
  if (result && result.cached && result.cached_at) {
    cachedAt = new Date(result.cached_at).toLocaleString('da-DK', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }
  let generatedAt = null;
  if (result && result.generated_at_iso) {
    generatedAt = new Date(result.generated_at_iso).toLocaleString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  const dataQuality = (result && result.data_quality) || [];
  const recommendations = (result && result.recommendations) || [];
  const summary = (result && result.summary) || '';
  const backdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
         onClick={backdropClick}
         data-testid="ai-analysis-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b bg-gradient-to-r from-purple-50 to-teal-50">
          <div className="flex items-center gap-3">
            <Sparkles className="h-6 w-6 text-purple-600" />
            <div>
              <h2 className="text-lg font-bold text-slate-800">AI-analyse af Salgseksperimentet</h2>
              {cachedAt ? <p className="text-xs text-slate-500">Cached fra {cachedAt}</p> : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {result ? (
              <Button size="sm" variant="outline" onClick={onRefresh} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            ) : null}
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="ai-modal-close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {loading && !result ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-purple-600 mb-3" />
              <p className="text-slate-600">Analyserer tal med GPT-4o...</p>
              <p className="text-xs text-slate-400 mt-1">Kan tage 15-30 sekunder</p>
            </div>
          ) : null}
          {error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              <p className="font-medium">Fejl</p>
              <p className="text-sm">{error}</p>
            </div>
          ) : null}
          {result && !loading ? (
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <BarChart3 className="h-5 w-5 text-slate-700" />
                  <h3 className="font-semibold text-slate-800">Overordnet konklusion</h3>
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{summary}</p>
              </div>
              {dataQuality.length > 0 ? (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                    <h3 className="font-semibold text-slate-800">Data-kvalitet</h3>
                  </div>
                  <ul className="space-y-2">
                    {dataQuality.map((q, i) => (
                      <li key={i} className="text-sm text-slate-700 flex gap-2">
                        <span className="text-amber-600 flex-shrink-0">•</span>
                        <span>{q}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {recommendations.length > 0 ? (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="h-5 w-5 text-yellow-500" />
                    <h3 className="font-semibold text-slate-800">Anbefalinger</h3>
                  </div>
                  <div className="space-y-3">
                    {recommendations.map((rec, i) => (
                      <AiRecommendation key={i} rec={rec} />
                    ))}
                  </div>
                </div>
              ) : null}
              {generatedAt ? (
                <p className="text-xs text-slate-400 text-right">Genereret {generatedAt}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Metric explanations
const metricExplanations = {
  conversion_uplift: {
    title: "Konverteringsløft",
    description: "Viser hvor meget bedre (eller dårligere) Gruppe A (Coaching) performer sammenlignet med Gruppe B (Kontrol).",
    formula: "((Konverteringsrate A - Konverteringsrate B) / Konverteringsrate B) × 100",
    example: "Hvis A har 5% konvertering og B har 4%, er løftet: ((5-4)/4) × 100 = +25%",
    interpretation: "Positivt tal = Coaching virker. Negativt tal = Kontrol performer bedre."
  },
  conversion_rate: {
    title: "Konverteringsrate",
    description: "Andelen af besøg der resulterer i en ordre.",
    formula: "(Antal ordrer / Antal besøg) × 100",
    example: "31 ordrer ud af 819 besøg = 3.79%",
    interpretation: "Jo højere, jo bedre. Sammenlign A og B for at se coaching-effekten."
  },
  avg_order_value: {
    title: "Gennemsnitlig ordreværdi",
    description: "Den gennemsnitlige værdi af ordrer i gruppen.",
    formula: "Total omsætning / Antal ordrer",
    example: "666.746 DKK / 31 ordrer = 21.507 DKK",
    interpretation: "Viser om coaching påvirker størrelsen af ordrer."
  },
  total_visits: {
    title: "Total besøg",
    description: "Antal kundebesøg i hver gruppe. Bruges som grundlag for alle beregninger.",
    formula: "Tælling af alle bookings tildelt gruppen",
    example: "Gruppe A: 819 besøg, Gruppe B: 835 besøg",
    interpretation: "Bør være nogenlunde lige for at sammenligningen er fair."
  },
  orders: {
    title: "Ordrer",
    description: "Antal gennemførte ordrer (konverteringer) i hver gruppe.",
    formula: "Tælling af bookings hvor kunde har afgivet ordre i Magento",
    example: "Gruppe A: 31 ordrer, Gruppe B: 33 ordrer",
    interpretation: "Rå tal - brug konverteringsrate for fair sammenligning."
  },
  total_revenue: {
    title: "Total omsætning",
    description: "Samlet omsætning fra alle ordrer i gruppen.",
    formula: "Sum af alle ordreværdier",
    example: "Gruppe A: 666.746 DKK fra 31 ordrer",
    interpretation: "Viser den faktiske forretningsværdi af hver gruppe."
  },
  survey_sent: {
    title: "Surveys sendt",
    description: "Antal kundeundersøgelser der er sendt ud til kunder i gruppen.",
    formula: "Tælling af surveys med status 'sendt'",
    example: "956 surveys sendt samlet",
    interpretation: "Viser hvor mange kunder der har modtaget spørgeskema."
  },
  survey_completed: {
    title: "Surveys besvaret",
    description: "Antal kundeundersøgelser der er blevet udfyldt.",
    formula: "Tælling af surveys med submitted_at dato",
    example: "482 surveys besvaret ud af 956 sendt = 50%",
    interpretation: "Høj svarprocent = bedre data om kundetilfredshed."
  },
  coaching_generated: {
    title: "Coaching genereret",
    description: "Antal gange AI-coaching er blevet genereret baseret på kundesvar.",
    formula: "Tælling af coaching-dokumenter oprettet",
    example: "450 coaching-tips genereret",
    interpretation: "Kun relevant for Gruppe A (Coaching-gruppen)."
  },
  seller_conversion: {
    title: "Sælger konverteringsrate",
    description: "Konverteringsrate for den enkelte sælger opdelt på gruppe.",
    formula: "(Sælgers ordrer i gruppe / Sælgers besøg i gruppe) × 100",
    example: "AL: 2.13% i A, 3.77% i B → B performer bedre for denne sælger",
    interpretation: "Viser om coaching hjælper den specifikke sælger."
  }
};

// Explanation overlay component
const ExplanationOverlay = ({ metric, onClose }) => {
  const explanation = metricExplanations[metric];
  if (!explanation) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
            <HelpCircle className="h-5 w-5 text-teal-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-800">{explanation.title}</h3>
        </div>
        
        <div className="space-y-4">
          <div>
            <p className="text-slate-600">{explanation.description}</p>
          </div>
          
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm font-medium text-slate-500 mb-1">Formel</p>
            <p className="font-mono text-sm text-slate-700">{explanation.formula}</p>
          </div>
          
          <div className="bg-teal-50 rounded-lg p-4">
            <p className="text-sm font-medium text-teal-600 mb-1">Eksempel</p>
            <p className="text-sm text-teal-700">{explanation.example}</p>
          </div>
          
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-slate-500 mb-1">Fortolkning</p>
            <p className="text-slate-600">{explanation.interpretation}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ExperimentDashboardPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [stats, setStats] = useState(null);
  const [sellerStats, setSellerStats] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [message, setMessage] = useState(null);
  const [activeExplanation, setActiveExplanation] = useState(null);
  
  // AI analysis
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState(null);
  
  const runAiAnalysis = async (forceRefresh = false) => {
    setAiOpen(true);
    setAiLoading(true);
    setAiError(null);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (forceRefresh) params.append('force_refresh', 'true');
      const res = await axios.post(
        `${API}/experiment/ai-analysis?${params}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 }
      );
      setAiResult(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'AI-analyse fejlede';
      setAiError(detail);
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const [statsRes, sellersRes] = await Promise.all([
        axios.get(`${API}/experiment/stats?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/experiment/sellers?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setStats(statsRes.data);
      setSellerStats(sellersRes.data);
    } catch (err) {
      console.error('Failed to fetch experiment data:', err);
      setMessage({ type: 'error', text: 'Kunne ikke hente data' });
    } finally {
      setLoading(false);
    }
  };

  const syncExistingData = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/experiment/sync-existing`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      setMessage({ 
        type: 'success', 
        text: `Synkronisering fuldført! ${d.bookings_processed || d.bookings_updated || 0} bookings, ${d.surveys_sent || 0} surveys sendt, ${d.surveys_completed || 0} surveys besvaret.`
      });
      fetchData();
    } catch (err) {
      console.error('Failed to sync:', err);
      setMessage({ type: 'error', text: 'Synkronisering fejlede' });
    } finally {
      setSyncing(false);
    }
  };

  const repairData = async () => {
    setRepairing(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/experiment/repair-data`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      setMessage({ 
        type: 'success', 
        text: `Data repareret! ${d.records_reset} nulstillet. Faktiske tal: ${d.actual_surveys_sent} sendt, ${d.actual_surveys_completed} besvaret, ${d.actual_coaching_generated} coaching.`
      });
      fetchData();
    } catch (err) {
      console.error('Failed to repair:', err);
      setMessage({ type: 'error', text: 'Data-reparation fejlede' });
    } finally {
      setRepairing(false);
    }
  };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const res = await axios.get(`${API}/experiment/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Convert to CSV
      const data = res.data.data;
      if (!data.length) {
        setMessage({ type: 'error', text: 'Ingen data at eksportere' });
        return;
      }

      const headers = Object.keys(data[0]);
      const csv = [
        headers.join(','),
        ...data.map(row => 
          headers.map(h => {
            const val = row[h];
            if (val === null || val === undefined) return '';
            if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
            return val;
          }).join(',')
        )
      ].join('\n');

      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `experiment_data_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Failed to export:', err);
      setMessage({ type: 'error', text: 'Eksport fejlede' });
    }
  };

  const StatCard = ({ title, valueA, valueB, suffix = '', icon: Icon, highlight = false, metricKey, onExplain }) => {
    const diff = valueA - valueB;
    const isPositive = diff > 0;
    
    return (
      <Card 
        className={`${highlight ? 'border-2 border-teal-500' : ''} cursor-pointer hover:shadow-lg transition-shadow relative group`}
        onClick={() => onExplain && onExplain(metricKey)}
      >
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <HelpCircle className="h-4 w-4 text-slate-400" />
        </div>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
            {Icon && <Icon className="h-4 w-4" />}
            {title}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-400 mb-1">Gruppe A (Coaching)</p>
              <p className="text-2xl font-bold text-teal-600">{valueA}{suffix}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Gruppe B (Kontrol)</p>
              <p className="text-2xl font-bold text-slate-600">{valueB}{suffix}</p>
            </div>
          </div>
          {diff !== 0 && (
            <div className={`mt-3 flex items-center gap-1 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {isPositive ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              <span>{isPositive ? '+' : ''}{diff.toFixed(2)}{suffix} forskel</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const groupA = stats?.group_a || {};
  const groupB = stats?.group_b || {};

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FlaskConical className="h-8 w-8 text-teal-600" />
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Sales Coaching Experiment</h1>
              <p className="text-slate-500">A/B test af coaching-systemets effekt på salg</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => runAiAnalysis(false)}
              className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700 text-white"
              data-testid="ai-analyze-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Analysér med AI
            </Button>
            <Button variant="outline" onClick={repairData} disabled={repairing} className="border-amber-300 text-amber-700 hover:bg-amber-50">
              {repairing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Reparer data
            </Button>
            <Button variant="outline" onClick={syncExistingData} disabled={syncing}>
              {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Synkroniser historik
            </Button>
            <Button variant="outline" onClick={exportCSV}>
              <Download className="h-4 w-4 mr-2" />
              Eksporter CSV
            </Button>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`p-3 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
            {message.text}
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <Label htmlFor="startDate">Fra dato</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="endDate">Til dato</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <Button onClick={fetchData}>
                Filtrer
              </Button>
              <Button variant="outline" onClick={() => { setStartDate(''); setEndDate(''); fetchData(); }}>
                Nulstil
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Overall Results */}
        <div>
          <h2 className="text-lg font-semibold text-slate-700 mb-4">Overordnede Resultater</h2>
          <p className="text-sm text-slate-500 mb-4">Klik på et kort for at se forklaring på beregningen</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card 
              className="border-2 border-teal-500 bg-teal-50 cursor-pointer hover:shadow-lg transition-shadow relative group"
              onClick={() => setActiveExplanation('conversion_uplift')}
            >
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <HelpCircle className="h-4 w-4 text-teal-600" />
              </div>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-sm text-teal-600 mb-2">
                  <TrendingUp className="h-4 w-4" />
                  Konverteringsløft
                </div>
                <p className="text-4xl font-bold text-teal-700">
                  {stats?.conversion_uplift_percentage > 0 ? '+' : ''}{stats?.conversion_uplift_percentage || 0}%
                </p>
                <p className="text-sm text-teal-600 mt-1">
                  Gruppe A vs. Gruppe B
                </p>
              </CardContent>
            </Card>

            <StatCard 
              title="Konverteringsrate" 
              valueA={groupA.conversion_rate} 
              valueB={groupB.conversion_rate}
              suffix="%"
              icon={ShoppingCart}
              highlight
              metricKey="conversion_rate"
              onExplain={setActiveExplanation}
            />

            <StatCard 
              title="Gns. ordreværdi" 
              valueA={groupA.avg_order_value} 
              valueB={groupB.avg_order_value}
              suffix=" DKK"
              icon={BarChart3}
              metricKey="avg_order_value"
              onExplain={setActiveExplanation}
            />

            <StatCard 
              title="Total besøg" 
              valueA={groupA.total_visits} 
              valueB={groupB.total_visits}
              icon={Users}
              metricKey="total_visits"
              onExplain={setActiveExplanation}
            />
          </div>
        </div>

        {/* Engagement Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Survey Engagement */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveExplanation('survey_sent')}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-600" />
                Survey Engagement (Kun Gruppe A)
                <HelpCircle className="h-4 w-4 text-slate-400 ml-auto" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-700">{groupA.surveys_sent}</p>
                  <p className="text-sm text-slate-500">Sendt</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-700">{groupA.surveys_completed}</p>
                  <p className="text-sm text-slate-500">Udfyldt</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-700">{groupA.survey_completion_rate}%</p>
                  <p className="text-sm text-green-600">Svarprocent</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Coaching Usage */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setActiveExplanation('coaching_generated')}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-purple-600" />
                Coaching Anvendelse (Kun Gruppe A)
                <HelpCircle className="h-4 w-4 text-slate-400 ml-auto" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-700">{groupA.coaching_generated}</p>
                  <p className="text-sm text-slate-500">Genereret</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-700">{groupA.coaching_opened}</p>
                  <p className="text-sm text-slate-500">Åbnet</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-700">{groupA.coaching_open_rate}%</p>
                  <p className="text-sm text-purple-600">Åbningsrate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Seller Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-slate-600" />
              Sælgeranalyse
              <button 
                onClick={() => setActiveExplanation('seller_conversion')}
                className="ml-auto"
              >
                <HelpCircle className="h-4 w-4 text-slate-400 hover:text-teal-600" />
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sellerStats.length === 0 ? (
              <p className="text-slate-500 text-center py-8">Ingen sælgerdata endnu. Klik "Synkroniser historik" for at indlæse data.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Sælger</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Besøg (A)</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Besøg (B)</th>
                      <th className="text-center py-3 px-4 font-medium text-teal-600">Konv. A</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Konv. B</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Gns. A</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Gns. B</th>
                      <th className="text-center py-3 px-4 font-medium text-green-600">Forskel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sellerStats.map((seller, idx) => {
                      const convDiff = seller.conversion_a - seller.conversion_b;
                      return (
                        <tr key={idx} className="border-b hover:bg-slate-50">
                          <td className="py-3 px-4 font-medium">{seller.seller_name}</td>
                          <td className="text-center py-3 px-4">{seller.visits_group_a}</td>
                          <td className="text-center py-3 px-4">{seller.visits_group_b}</td>
                          <td className="text-center py-3 px-4 text-teal-600 font-medium">{seller.conversion_a}%</td>
                          <td className="text-center py-3 px-4">{seller.conversion_b}%</td>
                          <td className="text-center py-3 px-4">{seller.avg_order_a.toLocaleString()} DKK</td>
                          <td className="text-center py-3 px-4">{seller.avg_order_b.toLocaleString()} DKK</td>
                          <td className={`text-center py-3 px-4 font-medium ${convDiff > 0 ? 'text-green-600' : convDiff < 0 ? 'text-red-600' : ''}`}>
                            {convDiff > 0 ? '+' : ''}{convDiff.toFixed(1)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Experiment Groups Explanation */}
        <Card className="bg-slate-100 border-slate-200">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-slate-700 mb-2">Om Eksperimentet</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-slate-600">
              <div>
                <p className="font-medium text-teal-600 mb-1">Gruppe A (Behandling)</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Modtager survey før besøg</li>
                  <li>Modtager AI-genereret coaching</li>
                  <li>Sælger får SMS med forberedelsestips</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-slate-600 mb-1">Gruppe B (Kontrol)</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Ingen survey</li>
                  <li>Ingen coaching</li>
                  <li>Normal salgsproces</li>
                </ul>
              </div>
            </div>
            <p className="mt-4 text-xs text-slate-500">
              Booking-ID'er med lige sidste ciffer → Gruppe A | Ulige → Gruppe B
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Explanation Overlay */}
      {activeExplanation && (
        <ExplanationOverlay 
          metric={activeExplanation} 
          onClose={() => setActiveExplanation(null)} 
        />
      )}
      
      {/* AI Analysis Modal */}
      <AiAnalysisModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        loading={aiLoading}
        result={aiResult}
        error={aiError}
        onRefresh={() => runAiAnalysis(true)}
      />
    </div>
  );
};

export default ExperimentDashboardPage;
